import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { DashboardSummary } from '@brechoflow/contracts';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useSession } from '@/features/auth/session';
import { http } from '@/lib/api';
import { detectPreset, presetRange, resolveRange, validateRange, type Preset } from './kpi';

const parsers = {
  from: parseAsString,
  to: parseAsString,
  branchId: parseAsString,
  channel: parseAsString,
  categoryId: parseAsString,
};

/** Filtros do dashboard, todos na URL. Sem `from`/`to` vale o mês corrente (não vai para a URL). */
export function useDashboardFilters() {
  const [raw, setRaw] = useQueryStates(parsers, { clearOnDefault: true });
  const range = useMemo(() => resolveRange(raw.from, raw.to), [raw.from, raw.to]);
  const preset = useMemo(() => detectPreset(range), [range]);
  const rangeError = useMemo(() => validateRange(range.from, range.to), [range]);

  const setPreset = useCallback(
    (p: Preset) => {
      if (p === 'month') return void setRaw({ from: null, to: null });
      const r = presetRange(p);
      return void setRaw({ from: r.from, to: r.to });
    },
    [setRaw],
  );
  const setFrom = useCallback((v: string | null) => void setRaw({ from: v ?? null, to: raw.to ?? range.to }), [setRaw, raw.to, range.to]);
  const setTo = useCallback((v: string | null) => void setRaw({ to: v ?? null, from: raw.from ?? range.from }), [setRaw, raw.from, range.from]);
  const setBranch = useCallback((v: string) => void setRaw({ branchId: v || null }), [setRaw]);
  const setChannel = useCallback((v: string) => void setRaw({ channel: v || null }), [setRaw]);
  const setCategory = useCallback((v: string) => void setRaw({ categoryId: v || null }), [setRaw]);

  return {
    range,
    preset,
    rangeError,
    branchId: raw.branchId ?? '',
    channel: raw.channel ?? '',
    categoryId: raw.categoryId ?? '',
    setPreset,
    setFrom,
    setTo,
    setBranch,
    setChannel,
    setCategory,
  };
}

export type DashboardFilters = ReturnType<typeof useDashboardFilters>;

function useOrgId() {
  return useSession().membership?.organizationId ?? 'none';
}

export function useDashboardSummary(f: Pick<DashboardFilters, 'range' | 'branchId' | 'channel' | 'categoryId' | 'rangeError'>) {
  const org = useOrgId();
  const params = { from: f.range.from, to: f.range.to, branchId: f.branchId, channel: f.channel, categoryId: f.categoryId };
  return useQuery({
    queryKey: ['dashboard', org, 'summary', params],
    queryFn: ({ signal }) => http.get<DashboardSummary>('/dashboard/summary', params, signal),
    enabled: !f.rangeError,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useBranches() {
  const org = useOrgId();
  return useQuery({
    queryKey: ['dashboard', org, 'branches'],
    queryFn: () => http.get<{ id: string; name: string; active?: boolean }[]>('/org/branches'),
    staleTime: 5 * 60_000,
  });
}

export function useCategoryOptions() {
  const org = useOrgId();
  return useQuery({
    queryKey: ['dashboard', org, 'categories'],
    queryFn: () => http.get<{ id: string; name: string; active: boolean }[]>('/product-categories'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
